import React, {useState} from "react";
import { AppDispatch } from "../../../redux/store";
import { useDispatch } from "react-redux";
import CommandSelect from "../../../components/common/UserSelect/CommandSelect";
import { addSpaCommands } from "../../../redux/actions/commands.actions";
import { useNavigate } from "react-router-dom";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import ModalShell, { ModalLoading } from "../../../components/common/Modals/ModalShell";
import { AlertIcon, CheckCircleIcon, ChevronRightIcon } from "../../../components/common/Icons/Icons";
import "./AddSpaCommandsModal.css";

const AddSpaCommandsModal: React.FC<ModalProps> = ({open, setOpen, selection}) => {
    const spas = selection??[];
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const [selectedCommand, setSelectedCommand] = useState<any>(null);
    const [selectedLogLevel, setSelectedLogLevel] = useState<any>('INFO');
    const [selectedService, setSelectedService] = useState<any>('');
    const [selectedFirmware, setSelectedFirmware] = useState<any>(null);
    const [selectedVersion, setSelectedVersion] = useState<any>(null);

    const [submitted, setSubmitted] = useState(false);
    const [results, setResults] = useState<any[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);

    const handleClose = () => {
        setSubmitted(false);
        setSelectedCommand(null);
        setSelectedLogLevel('INFO');
        setSelectedService('');
        setResults([]);
        setIsProcessing(false);
        setOpen(false);
    };

    const handleResetForm = () => {
        setSubmitted(false);
        setResults([]);
        setIsProcessing(false);
        // Keep selectedCommand, selectedLogLevel, and selectedService so user can modify if needed
    };

    const handleSubmit = async () => {
        if((spas!==undefined && spas.length>0)&&(selectedCommand)){
            setSubmitted(true);
            setIsProcessing(true);
            setResults([]);

            // Process all commands in parallel
            const promises = spas.map(async (spa, index) => {
                try {
                    await dispatch(addSpaCommands({
                        command_id: selectedCommand.id,
                        spa_id: spa.id,
                        log_level: selectedLogLevel,
                        service: selectedService,
                        firmware: selectedFirmware,
                        version: selectedVersion
                    }));
                    return { spa, status: 'success', message: 'Command sent successfully' };
                } catch (error: any) {
                    return {
                        spa,
                        status: 'error',
                        message: error?.response?.data?.data?.message || error?.message || 'Command failed'
                    };
                }
            });

            const newResults = await Promise.all(promises);
            setResults(newResults);
            setIsProcessing(false);
        }
    }

if(!submitted) {
    return (
        <ModalShell
            open={open}
            onClose={handleClose}
            size="lg"
            title={spas.length > 1 ? `Send a command to ${spas.length} spas` : `Send a command to ${spas[0]?.nick_name ?? 'spa'}`}
            subtitle="It may take up to 15 seconds for the command to reach the spa."
            actions={
                <>
                    <button className="btn" onClick={handleClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSubmit} disabled={selectedCommand===null}>Send command</button>
                </>
            }
        >
            <CommandSelect setSelectedCommand={setSelectedCommand} setSelectedLogLevel={setSelectedLogLevel} setSelectedService={setSelectedService} setSelectedFirmware={setSelectedFirmware} setSelectedVersion={setSelectedVersion} spas={spas}/>
        </ModalShell>
    );
} else if (submitted && isProcessing) {
    return (
        <ModalShell open={open} onClose={handleClose} size="sm" busy title="Sending command…">
            <ModalLoading/>
        </ModalShell>
    );
} else {
    // Calculate summary counts
    const successCount = results.filter((res: any) => res.status === 'success').length;
    const errorCount = results.filter((res: any) => res.status === 'error').length;
    const totalCount = results.length;
    // Successes first, then failures
    const ordered = [
        ...results.filter((res: any) => res.status === 'success'),
        ...results.filter((res: any) => res.status === 'error'),
    ];

    return (
        <ModalShell
            open={open}
            onClose={handleClose}
            tone={totalCount === 0 ? undefined : errorCount === 0 ? "success" : successCount === 0 ? "error" : "warning"}
            title={errorCount === 0 ? "Command sent" : successCount === 0 ? "Command failed" : "Command partly sent"}
            subtitle={totalCount > 0 ? "Open a spa to track the command's progress." : undefined}
            actions={
                <>
                    <button className="btn btn-ghost dlg-actions-start" onClick={handleResetForm}>
                        {errorCount > 0 ? 'Try again' : 'Send another'}
                    </button>
                    <button className="btn btn-primary" onClick={handleClose}>Done</button>
                </>
            }
        >
            {totalCount === 0 ? (
                <p className="dlg-muted">No results available</p>
            ) : (
                <>
                    <div className="cmd-summary">
                        {successCount > 0 && <span className="cmd-count good"><CheckCircleIcon/>{successCount} successful</span>}
                        {errorCount > 0 && <span className="cmd-count bad"><AlertIcon/>{errorCount} failed</span>}
                        <span className="cmd-count">{totalCount} total</span>
                    </div>

                    <ul className="cmd-results">
                        {ordered.map((res: any) => (
                            <li key={res?.spa?.id}>
                                <a
                                    className={`cmd-result ${res.status === 'success' ? 'good' : 'bad'}`}
                                    href={`/spa/${res?.spa?.id}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/spa/${res?.spa?.id}`);
                                    }}
                                >
                                    <span className="cmd-result-icon">{res.status === 'success' ? <CheckCircleIcon/> : <AlertIcon/>}</span>
                                    <span className="cmd-result-text">
                                        <span className="cmd-result-name">{res?.spa?.nick_name || res?.spa?.id}</span>
                                        <span className="cmd-result-msg">{res?.message || "Command failed"}</span>
                                    </span>
                                    <ChevronRightIcon className="cmd-result-chevron"/>
                                </a>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </ModalShell>
    );
}

};

export default AddSpaCommandsModal;
